import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { TTS_MAX_TEXT_LENGTH } from "../../Apihander/Tts/Tts";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { PageHeader, fmt } from "../Notes/notesShared";
import { AiIc } from "./aiShared";
import "../Notes/notes.css";
import "./aiTools.css";

// Editor on the left; generating and result states share the right panel.
const TextToSpeech = ({ source, onClose, onGenerate, onGenerateAgain }) => {
    const [stage, setStage] = useState("editor");
    const [text, setText] = useState(source.text || "");
    const [progress, setProgress] = useState(8);
    const [result, setResult] = useState(null);
    const [generatedText, setGeneratedText] = useState("");
    const [playing, setPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const textareaRef = useRef(null);
    const audioRef = useRef(null);

    useEffect(() => {
        if (stage === "editor" && !source.text) textareaRef.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Creeps toward done while the request runs, since the service reports no progress.
    useEffect(() => {
        if (stage !== "run") return undefined;
        const id = setInterval(() => setProgress((value) => Math.min(95, value + 9)), 220);
        return () => clearInterval(id);
    }, [stage]);

    useEffect(() => () => { if (result?.url) URL.revokeObjectURL(result.url); }, [result]);

    const handleClear = () => {
        setText("");
        textareaRef.current?.focus();
    };

    const handleGenerate = async () => {
        const trimmed = text.trim();
        if (!trimmed) {
            errorToast("Enter some text first");
            return;
        }
        setProgress(8);
        setPlaying(false);
        setPosition(0);
        setStage("run");
        const generated = await onGenerate(trimmed);
        if (!generated) {
            setStage(result ? "result" : "editor");
            return;
        }
        setResult({ ...generated, url: URL.createObjectURL(generated.blob) });
        setGeneratedText(trimmed);
        setDuration(generated.duration || 0);
        setStage("result");
    };

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) audio.play(); else audio.pause();
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const bar = e.currentTarget.getBoundingClientRect();
        audio.currentTime = Math.min(1, Math.max(0, (e.clientX - bar.left) / bar.width)) * duration;
    };

    const handleSave = () => {
        const anchor = document.createElement("a");
        anchor.href = result.url;
        anchor.download = result.fileName;
        anchor.click();
        successToast("Speech downloaded");
    };

    const busy = stage === "run";
    // Nothing new to generate until the text differs from the last run.
    const unchanged = Boolean(result) && text.trim() === generatedText;
    const pct = duration ? (position / duration) * 100 : 0;

    return (
        <div className="nt vr-tone-violet">
            <PageHeader title="Text To Speech" onBack={busy ? undefined : onClose} icon={<Icon icon="lucide:audio-lines" />} tone="violet" />
            <div className="nt-content ai-wide">
                <div className="ai-split">
                    <div className="ai-split-col">
                        <div className="ai-editor-head">
                            <span className="nt-caps">Your text</span>
                            <button type="button" className="nt-linkbtn ai-clear" onClick={handleClear}>Clear</button>
                        </div>
                        <textarea
                            ref={textareaRef}
                            className="ai-ta"
                            placeholder="Enter your text…"
                            value={text}
                            maxLength={TTS_MAX_TEXT_LENGTH}
                            onChange={(e) => setText(e.target.value)}
                        />
                        <div className="ai-editor-foot nt-tiny-muted">
                            <span>{source.label}</span>
                            <span>{text.length.toLocaleString()} / {TTS_MAX_TEXT_LENGTH.toLocaleString()} characters</span>
                        </div>
                        <button
                            type="button"
                            className="nt-btn block ai-generate"
                            disabled={!text.trim() || busy || unchanged}
                            onClick={handleGenerate}
                        >
                            <AiIc name="speaker" size="s" />Generate
                        </button>
                    </div>

                    <div className="ai-split-col">
                        <div className="ai-editor-head">
                            <span className="nt-caps">Generated speech</span>
                        </div>
                        <div className="nt-card ai-player ai-out">
                            {stage === "editor" && (
                                <div className="ai-out-empty">
                                    <span className="nt-av tone"><AiIc name="speaker" /></span>
                                    <h5>Your speech will appear here</h5>
                                    <p>Enter your text and press Generate</p>
                                </div>
                            )}
                            {busy && (
                                <div className="ai-proc">
                                    <div className="ai-orb" aria-hidden="true">
                                        <span className="ai-orb-spin" />
                                        <span className="ai-orb-core"><AiIc name="speaker" size="l" /></span>
                                    </div>
                                    <h5>Generating audio…</h5>
                                    <p>Turning {text.trim().length.toLocaleString()} characters into speech</p>
                                    <div className="ai-bar"><i style={{ width: `${progress}%` }} /></div>
                                </div>
                            )}
                            {stage === "result" && (
                                <>
                                    <audio
                                        ref={audioRef}
                                        src={result.url}
                                        onPlay={() => setPlaying(true)}
                                        onPause={() => setPlaying(false)}
                                        onEnded={() => setPlaying(false)}
                                        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                                        onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setDuration(e.currentTarget.duration); }}
                                    />
                                    <div className="ai-result">
                                        <div className="ai-tick"><Icon icon="lucide:audio-lines" /></div>
                                        <h5>Your speech is ready</h5>
                                    </div>
                                    <div className="nt-seek" onClick={handleSeek}>
                                        <i style={{ width: `${pct}%` }} />
                                        <b style={{ left: `${pct}%` }} />
                                    </div>
                                    <div className="nt-times"><span>{fmt(position)}</span><span>{fmt(duration)}</span></div>
                                    <div className="nt-pcontrols">
                                        <button type="button" className="nt-pbtn main" onClick={togglePlay}>
                                            <AiIc name={playing ? "pause" : "play"} size="l" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="ai-editor-foot nt-tiny-muted">
                            <span>{result ? `${fmt(duration)} • MP3` : "MP3"}</span>
                            <span>{result && !busy ? "Saved to My Recordings" : ""}</span>
                        </div>
                        <div className="ai-row">
                            <button type="button" className="nt-btn outline" disabled={stage !== "result"} onClick={handleSave}><AiIc name="down" size="s" />Save</button>
                            <button type="button" className="nt-btn outline" disabled={stage !== "result"} onClick={onGenerateAgain}>Generate Again</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TextToSpeech;
